import { cn } from '@/lib/utils';
import type { PillTone } from './tone';

/**
 * The "minis" strip (design prototype `.minis`): a dense row of centered
 * value/label cells separated by 1px hairlines, with a bottom border under
 * the whole strip. Cells with no value render an em-dash in muted ink — the
 * prototype shows uninstrumented retention cohorts exactly this way rather
 * than hiding them.
 */
export interface MiniCell {
  value: string | null | undefined;
  label: string;
  tone?: PillTone;
}

const VALUE_TONES: Partial<Record<PillTone, string>> = {
  ok: 'text-success',
  warn: 'text-warning',
  crit: 'text-destructive',
};

export default function MiniStrip({ cells }: { cells: MiniCell[] }) {
  if (cells.length === 0) return null;
  return (
    // Column count follows the cell count (capped at 6). The fixed six-column
    // grid painted its `bg-border` backdrop across every track without a cell,
    // so a strip reduced to one metric rendered as one value beside a grey
    // slab spanning the rest of the card.
    <div
      className="mb-4.5 grid gap-px border-b border-border bg-border"
      style={{ gridTemplateColumns: `repeat(${Math.min(cells.length, 6)}, minmax(0, 1fr))` }}
    >
      {cells.map((c) => {
        const empty = c.value == null || c.value === '';
        return (
          <div key={c.label} className="bg-card px-3 pb-3.5 pt-2.5 text-center">
            <div
              className={cn(
                'text-2xl font-bold tracking-[-0.02em] tabular-nums',
                empty ? 'text-muted-foreground/40' : (c.tone && VALUE_TONES[c.tone]) || 'text-foreground',
              )}
            >
              {empty ? '–' : c.value}
            </div>
            <div className="mt-1 text-[11.5px] text-muted-foreground">{c.label}</div>
          </div>
        );
      })}
    </div>
  );
}
